import { map } from "ramda";

import {
  deleteRequest,
  checkStatus,
  getRequest,
  parseStatusAndBody,
  postRequest,
  putRequest,
} from "helpers/requests";
import { HQ_WORKFORCE_SERVICE, mockPath } from "constants/app";
import {
  parseUnavailabilitySchema,
  parseUserSchema,
  encodeUnavailToJSON,
} from "helpers/transforms/pos_users";
import { IUnavailabilityBlock, Frequency, IPosUser } from "types/schedules";

interface IRoleResponse {
  payRate: string;
  name: string;
  category: string;
  uuid: string;
}

interface IPosUserResponse {
  uuid: string;
  firstName: string;
  lastName: string;
  roles: IRoleResponse[];
  email: string | null;
  phone: string | null;
}

type IPosUsersResponse = IPosUserResponse[];

function getPosUsers(location: string, prettyUrl: string): Promise<IPosUser[]> {
  const posUsersPath = mockPath(
    `${HQ_WORKFORCE_SERVICE}/${location}/users?store_pretty_url=${prettyUrl}`,
    "/users"
  );

  return fetch(posUsersPath, getRequest())
    .then((response) => parseStatusAndBody<IPosUsersResponse>(response))
    .then((parsedResponse: [number, IPosUsersResponse]) =>
      checkStatus<IPosUsersResponse>(parsedResponse)
    )
    .then(map(parseUserSchema));
}

interface IUnavailabilityBlockResponse {
  userId: string;
  date: string;
  startTimeHour: number;
  startTimeMinute: number;
  endTimeHour: number;
  endTimeMinute: number;
  frequency: Frequency | null;
  type: "UNAVAILABLE" | "EXCEPTION";
  uuid: string;
}

function getUnavailability(
  startDay: string,
  endDay: string,
  location: string
): Promise<IUnavailabilityBlock[]> {
  const unavailPath = mockPath(
    `${HQ_WORKFORCE_SERVICE}/${location}/users/unavailability`,
    "/unavailability"
  );
  const query = `?start_date=${encodeURIComponent(
    startDay
  )}&end_date=${encodeURIComponent(endDay)}`;

  return fetch(`${unavailPath}${query}`, getRequest())
    .then((response) =>
      parseStatusAndBody<IUnavailabilityBlockResponse[]>(response)
    )
    .then((parsedResponse: [number, IUnavailabilityBlockResponse[]]) =>
      checkStatus<IUnavailabilityBlockResponse[]>(parsedResponse)
    )
    .then(map(parseUnavailabilitySchema));
}

function postUnavailability(
  unavail: IUnavailabilityBlock,
  location: string
): Promise<IUnavailabilityBlock> {
  const postPath = mockPath(
    `${HQ_WORKFORCE_SERVICE}/${location}/users/unavailability/`,
    "/unavailability"
  );
  const unavailJSON = encodeUnavailToJSON(unavail);
  return fetch(postPath, postRequest(unavailJSON))
    .then((response) =>
      parseStatusAndBody<IUnavailabilityBlockResponse>(response)
    )
    .then((parsedResponse: [number, IUnavailabilityBlockResponse]) =>
      checkStatus<IUnavailabilityBlockResponse>(parsedResponse)
    )
    .then((resp) => parseUnavailabilitySchema(resp));
}

function putUnavailability(unavail: IUnavailabilityBlock, location: string) {
  const putPath = `${HQ_WORKFORCE_SERVICE}/${location}/users/unavailability/${
    unavail.uuid
  }`;

  const unavailJSON = encodeUnavailToJSON(unavail);
  return fetch(putPath, putRequest(unavailJSON))
    .then((response) =>
      parseStatusAndBody<IUnavailabilityBlockResponse>(response)
    )
    .then((parsedResponse: [number, IUnavailabilityBlockResponse]) =>
      checkStatus<IUnavailabilityBlockResponse>(parsedResponse)
    )
    .then((resp) => parseUnavailabilitySchema(resp));
}

function requestDeleteUnavailability(
  uuid: string,
  location: string
): Promise<void> {
  const deletePath = `${HQ_WORKFORCE_SERVICE}/${location}/users/unavailability/${uuid}`;
  return fetch(deletePath, deleteRequest()).then((response) =>
    checkStatus<void>([response.status, undefined])
  );
}

export {
  getPosUsers,
  IRoleResponse,
  IPosUserResponse,
  IPosUsersResponse,
  IUnavailabilityBlockResponse,
  getUnavailability,
  postUnavailability,
  putUnavailability,
  requestDeleteUnavailability,
};
